import { useCallback, useMemo } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, XAxis } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import {
  CHART_UNCLASSIFIED_LABEL,
  ExploreChartLoader,
  ExploreChartEmptyState,
} from "./explore-widgets-utils";
import { useExploreDistributionChart } from "./useExploreDistributionChart";
import { useSize } from "@/v2/shared/hooks/useSize";
import { useSlotWidth, barChartMargin } from "./ExploreBarChart";
import { TruncatedTick } from "./TruncatedTick";

interface ExploreStackedBarChartProps {
  source?: ExploreInteractionType;
  groupBy: string;
  splitBy?: string;
  filter?: Filter;
}

export function ExploreStackedBarChart({
  source,
  groupBy,
  splitBy,
  filter,
}: ExploreStackedBarChartProps) {
  const { ref, width } = useSize<HTMLDivElement>();

  const {
    items,
    isLoading,
    valueMap,
    chartConfig,
    itemToLabelMap,
    groupByLabelFormatter,
  } = useExploreDistributionChart(source, groupBy, splitBy, "volume", filter);

  const slotWidth = useSlotWidth(items.length, width);

  const chartData = useMemo(() => {
    return items.map(({ values, ...rest }) => ({
      ...rest,
      ...Object.fromEntries(values.map(([key, value]) => [key, value])),
    }));
  }, [items]);

  const xTickLabelFormatter = useCallback(
    (value: string) => {
      if (groupByLabelFormatter == null) {
        return value || CHART_UNCLASSIFIED_LABEL;
      }

      const label = itemToLabelMap.get(value);
      return groupByLabelFormatter(label) || CHART_UNCLASSIFIED_LABEL;
    },
    [itemToLabelMap, groupByLabelFormatter]
  );

  if (splitBy == null) {
    return <ExploreChartEmptyState variant="no-metric" />;
  }

  if (isLoading) {
    return <ExploreChartLoader />;
  }

  if (items.length === 0) {
    return <ExploreChartEmptyState variant="no-data" />;
  }

  return (
    <ChartContainer
      config={chartConfig}
      className="aspect-auto  w-full"
      ref={ref}
    >
      <BarChart accessibilityLayer data={chartData} margin={barChartMargin}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey={"value"}
          interval={0}
          minTickGap={6}
          tickLine={false}
          axisLine={false}
          height={48}
          tick={
            <TruncatedTick
              axis="x"
              anchor="middle"
              dy={8}
              slotWidth={slotWidth}
              pad={4}
              labelFormatter={xTickLabelFormatter}
            />
          }
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        <ChartLegend content={<ChartLegendContent />} />
        {Array.from(valueMap.keys()).map((value, index, array) => {
          const isFirst = index === 0;
          const isLast = index === array.length - 1;

          let radius: [number, number, number, number] = isFirst
            ? isLast
              ? [4, 4, 4, 4]
              : [0, 0, 4, 4]
            : isLast
            ? [4, 4, 0, 0]
            : [0, 0, 0, 0];

          return (
            <Bar
              unit={"volume"}
              key={`stacked-bar-chart-bar-${index}`}
              dataKey={value}
              stackId="a"
              fill={`var(--color-${value})`}
              radius={radius}
              maxBarSize={60}
            />
          );
        })}

        <Bar dataKey={"total"} stackId="a">
          <LabelList
            position="top"
            offset={12}
            className="font-mono font-medium tabular-nums text-foreground fill-foreground"
            fontSize={12}
            formatter={(value: number) => value.toLocaleString("en-US")}
          />
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}
